import type { QueryClient } from '@tanstack/react-query';

/** Корень всех запросов фильтра списка проектов: выдача, счётчики, варианты меню. */
export const PROJECT_FILTER_QUERY_ROOT = ['newdesign', 'project-filter'] as const;

/** Выдача и счётчики поиска по тексту живут вне корня фильтра. */
export const PROJECT_SEARCH_QUERY_ROOT = ['newdesign', 'projects-search'] as const;
export const PROJECT_SEARCH_COUNTS_QUERY_ROOT = ['newdesign', 'projects-search-counts'] as const;

/**
 * Список черновиков автора («Черновики» в новом дизайне). Не перезапрашивается на каждое событие проекта: realtime
 * правит его ids точечно по is_draft (useRealtimeSync), перезапрос — только при переподключении.
 */
export const PROJECT_DRAFTS_QUERY_ROOT = ['newdesign', 'project-drafts'] as const;

const PROJECT_LIST_REFRESH_ROOTS = [
  PROJECT_FILTER_QUERY_ROOT,
  PROJECT_SEARCH_QUERY_ROOT,
  PROJECT_SEARCH_COUNTS_QUERY_ROOT,
] as const;

export const PROJECT_LIST_REFRESH_DELAY_MS = 300;

/** Предел ожидания паузы: непрерывный поток событий всё равно обновляет список (не чаще одного лишнего перезапроса в 2 с). */
export const PROJECT_LIST_REFRESH_MAX_WAIT_MS = 2000;

/**
 * Пачка событий проектов (массовая правка, импорт) даёт один перезапрос: инвалидация откладывается
 * до паузы в delayMs, но не дольше maxWaitMs от первого необработанного события. Перезапрашиваются только запросы
 * с наблюдателями. Неудачный перезапрос оставляет в запросе прежние data, но ставит isError — показывать ли ошибку,
 * решает потребитель (см. listQueryView).
 */
export function createProjectListRefresher(
  queryClient: QueryClient,
  delayMs = PROJECT_LIST_REFRESH_DELAY_MS,
  maxWaitMs = PROJECT_LIST_REFRESH_MAX_WAIT_MS,
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let maxTimer: ReturnType<typeof setTimeout> | null = null;
  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    if (maxTimer !== null) clearTimeout(maxTimer);
    timer = null;
    maxTimer = null;
  };
  const flush = () => {
    cancel();
    for (const queryKey of PROJECT_LIST_REFRESH_ROOTS) {
      void queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
    }
  };
  return {
    schedule(): void {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(flush, delayMs);
      if (maxTimer === null) maxTimer = setTimeout(flush, maxWaitMs);
    },
    dispose: cancel,
  };
}
